/* eslint-disable max-lines-per-function */
import { ReactElement, useCallback } from 'react';
import { get } from 'lodash';

import { PractitionerId } from '@dreryk/egabinet-personnel-published-language-api';

import { TestableComponentProps, TextEditor } from '@dreryk/react-components';
import { FieldController, TranslatableText } from '@dreryk/react-common';
import { TextEditorValue } from '@dreryk/react-text-editor';

import { useAiAssistantAvailable } from '~libs.desktop.ai-assistant';

import { useRedactionContext } from '../../../common/visitForm/VisitFormContext';
import { VisitField } from '../../../common/visitForm/VisitFormContext.types';

import { useTextEditorAutosave, useVisitFormDictation } from './VisitFormTextEditor.hooks';
import { useVisitFormAiEdit } from './VisitFormTextEditorEdit.hooks';
import { OnVisitFormTextEditorAutoSave } from './VisitFormTextEditor.types';
import { StyledLockedEditor } from './VisitFormTextEditor.styled';

const VISIT_TEXT_EDITOR_MAX_HTML_CONTENT_LENGTH = 50000;
const VISIT_TEXT_EDITOR_MAX_CONTENT_LENGTH = 10000;

export const getVisitTextEditorMaxHTMLContentLength = (): typeof VISIT_TEXT_EDITOR_MAX_HTML_CONTENT_LENGTH =>
   VISIT_TEXT_EDITOR_MAX_HTML_CONTENT_LENGTH;

const CONTROLLER_NAME_TO_FIELD: Record<string, VisitField> = {
   interview: 'INTERVIEW',
   subjectiveAssessments: 'EXAMINATION',
   recommendations: 'RECOMMENDATIONS',
};

type VisitFormTextEditorProps = TestableComponentProps & {
   controller: FieldController<TextEditorValue>;
   label?: TranslatableText;
   modalLabel: TranslatableText;
   placeholder: TranslatableText;
   labelSuffix?: ReactElement;
   practitionerId: PractitionerId;
   onAutoSave: OnVisitFormTextEditorAutoSave;
};

export const VisitFormTextEditor = (props: VisitFormTextEditorProps): JSX.Element => {
   const { lockedField } = useRedactionContext();
   // eslint-disable-next-line @dreryk/check-experimental-to-do
   const isDictationAvailable = useAiAssistantAvailable();

   const field = CONTROLLER_NAME_TO_FIELD[props.controller.name] ?? 'INTERVIEW';
   const isLockedByRedaction = field === lockedField;

   const dictation = useVisitFormDictation({ field: field });
   const aiEdit = useVisitFormAiEdit({ field: field });

   const { isPendingDecision } = aiEdit;

   const { setTextEditorElement } = useTextEditorAutosave({
      practitionerId: props.practitionerId,
      onAutoSave: props.onAutoSave,
      isDirty: Boolean(get(props.controller.control!.formState.dirtyFields, props.controller.name)),
      value: props.controller.control!.watchInternal(props.controller.name) as TextEditorValue | null,
      disabled: dictation.isDictating || aiEdit.isProcessing || isPendingDecision,
   });

   const handleModalViewTextChange = useCallback(
      (value: TextEditorValue | null) => {
         props.controller.control!.setValue(props.controller.name, value, {
            shouldDirty: true,
            shouldValidate: true,
         });
      },
      [props.controller.control, props.controller.name]
   );

   const getModalViewInitialValue: () => TextEditorValue = useCallback(
      () => props.controller.control!.getValues(props.controller.name),
      [props.controller]
   );

   return (
      <StyledLockedEditor $locked={isLockedByRedaction}>
         <TextEditor
            placeholder={props.placeholder}
            label={props.label}
            controller={props.controller}
            testId={props.testId}
            elementRef={setTextEditorElement}
            labelSuffixIcon={props.labelSuffix}
            maxLength={VISIT_TEXT_EDITOR_MAX_CONTENT_LENGTH}
            maxHtmlLength={VISIT_TEXT_EDITOR_MAX_HTML_CONTENT_LENGTH}
            disabled={isLockedByRedaction}
         >
            <>
               {isDictationAvailable ? (
                  <>
                     <TextEditor.Dictation
                        testId={'dictation'}
                        onBeforeStart={dictation.handleBeforeStart}
                        onStart={dictation.handleStart}
                        onStop={dictation.handleStop}
                     />

                     <TextEditor.Decision
                        testId={'editDecision'}
                        onReject={aiEdit.handleReject}
                        onAccept={aiEdit.handleAccept}
                     >
                        {({ show }) => (
                           <TextEditor.Edit testId={'edit'} onEdit={aiEdit.handleEdit} onProcessed={show} />
                        )}
                     </TextEditor.Decision>
                  </>
               ) : null}

               {isPendingDecision ? null : (
                  <TextEditor.ModalView
                     testId={`${props.testId}ModalView`}
                     label={props.modalLabel}
                     placeholder={props.placeholder}
                     onChange={handleModalViewTextChange}
                     initialValue={getModalViewInitialValue}
                     maxLength={VISIT_TEXT_EDITOR_MAX_CONTENT_LENGTH}
                  />
               )}
            </>
         </TextEditor>
      </StyledLockedEditor>
   );
};
